import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpParams } from '@angular/common/http';
import { BaseHttpRepository } from './base-http.repository';
import { ProfileSelection, Profile, ImpersonationStart, ProfileSearchCriteria, ProfilesListResponse, CreateUserRequest, CreateUserResponse } from '../interfaces/api.interface';

/**
 * Profile Repository
 * Handles all profile-related HTTP calls
 * Pure data access layer - no business logic
 */
@Injectable({
  providedIn: 'root'
})
export class ProfileRepository extends BaseHttpRepository {
  
  /**
   * Get profiles for selection dropdown
   * @param name Optional name filter (case-insensitive partial match)
   * @param skip Number of records to skip (default: 0)
   * @param take Number of records to return (default: 100)
   */
  getProfilesForSelection(
    name?: string, 
    skip: number = 0, 
    take: number = 100
  ): Observable<ProfileSelection[]> {
    let params = new HttpParams();
    if (name) params = params.set('name', name);
    params = params.set('skip', skip.toString());
    params = params.set('take', take.toString());
    
    return this.get<ProfileSelection[]>('/profiles/for-selection', params);
  }

  /** Open a server-side impersonation session ("Ver como") on the target profile. */
  startImpersonation(profileId: string): Observable<ImpersonationStart> {
    return this.post<ImpersonationStart>('/impersonation', { profileId });
  }

  endImpersonation(): Observable<void> {
    return this.delete<void>('/impersonation');
  }

  /**
   * Get profiles list with pagination, sorting, and filtering
   * Admin-only endpoint
   */
  getProfiles(criteria: ProfileSearchCriteria = {}): Observable<ProfilesListResponse> {
    let params = new HttpParams();
    
    // Pagination
    params = params.set('skip', (criteria.skip ?? 0).toString());
    params = params.set('take', (criteria.limit ?? 25).toString());
    
    // Sorting
    if (criteria.orderBy) params = params.set('orderBy', criteria.orderBy);
    if (criteria.orderDirection) params = params.set('orderDirection', criteria.orderDirection);
    
    // Filters
    if (criteria.name) params = params.set('name', criteria.name);
    if (criteria.email) params = params.set('email', criteria.email);
    if (criteria.role) params = params.set('role', criteria.role);
    if (criteria.codigoanf) params = params.set('codigoanf', criteria.codigoanf);
    if (criteria.sfuser) params = params.set('sfuser', criteria.sfuser);
    if (criteria.sfaccount) params = params.set('sfaccount', criteria.sfaccount);
    if (criteria.sfsoftware) params = params.set('sfsoftware', criteria.sfsoftware);
    if (criteria.userid) params = params.set('userid', criteria.userid);
    
    return this.get<ProfilesListResponse>('/profiles', params);
  }

  /**
   * Create a new Salesforce User and linked Account
   * Admin-only endpoint
   */
  createUser(data: CreateUserRequest): Observable<CreateUserResponse> {
    return this.post<CreateUserResponse>('/users/create', data);
  }
}
